import axios from 'axios';
import { getMappingProducts, getProductMappings, saveProductMappings } from './productMappingApi';
import { normalizeDescription, structuralIssue } from '../utils/groupedProductMapping';
import { referenceId } from '../utils/productMapping';

export async function loadGroupedMappingData() {
  const [invoices, products] = await Promise.all([axios.get('/api/running'), getMappingProducts(undefined)]);
  if (!Array.isArray(invoices.data) || !Array.isArray(products)) throw new Error('Unexpected mapping data response.');
  return { invoices: invoices.data, products };
}
// Independent invoices commit separately; each invoice uses the existing atomic multi-item API.
export async function applyMappingPlan(plan, retry = false) {
  const results = [];
  for (const entry of plan) {
    let document;
    try {
      let expectedVersion = entry.expectedVersion;
      let rows = entry.rows;
      if (retry) {
        document = await getProductMappings(entry.invoiceId);
        const issue = structuralIssue(document);
        if (issue || document.organization !== entry.organization) throw new Error(issue || 'Invoice organization changed; inspect this invoice.');
        rows = entry.rows.filter(row => {
          const current = document.items.find(item => referenceId(item._id) === referenceId(row.itemId));
          if (!current || normalizeDescription(current.label) !== row.description) throw new Error('Invoice items changed; inspect and select again.');
          const currentId = referenceId(current.productId) || '';
          if (currentId === referenceId(entry.productId)) return false; // Already committed, including uncertain network outcomes.
          if (currentId !== row.originalProductId) throw new Error('Product mapping changed since selection; inspect and select again.');
          return true;
        });
        expectedVersion = document.__v ?? 0;
      }
      if (rows.length) await saveProductMappings(entry.invoiceId, { expectedVersion,
        mappings: rows.map(row => ({ itemId: row.itemId, productId: entry.productId, ...(row.confirmRemap ? { confirmRemap: true } : {}) })) });
      document = await getProductMappings(entry.invoiceId);
      results.push({ entry, document, success: true });
    } catch (error) {
      try { document = await getProductMappings(entry.invoiceId); } catch { /* Keep the explicit failure; never assume a write failed or retry it automatically. */ }
      results.push({ entry, document, success: false, error: error.response?.data?.error || error.message || 'Mapping failed; refresh and review.' });
    }
  }
  return results;
}
